import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Button, Card, List, Typography, Table } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { fetchAppointmentPreviousFiles } from 'services/api/appointments';
import { AppointmentFile as AppointmentFileRow } from 'types/api/appointments';

import { useAppointmentDetail } from 'pages/AppointmentsDetails/AppointmentDetailContext';
import { validateLink } from 'utils/link';
import DownloadFile from './DownloadFile';

const { Title } = Typography;

const Files: FC = () => {
  // translations
  const { t } = useTranslation();
  // navigation
  const params = useParams();
  const { appointment } = useAppointmentDetail();

  const [previousFiles, setPreviousFiles] = useState<AppointmentFileRow[]>([]);

  useEffect(() => {
    if (!params.id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchAppointmentPreviousFiles(params.id as string);
        if (!cancelled) setPreviousFiles(rows);
      } catch {
        if (!cancelled) setPreviousFiles([]);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [params.id]);

  const files: AppointmentFileRow[] = Array.isArray(appointment?.files)
    ? (appointment?.files as AppointmentFileRow[])
    : [];

  const links: string[] = Array.isArray(appointment?.links)
    ? (appointment?.links as string[])
    : [];

  const columns: ColumnProps<AppointmentFileRow>[] = useMemo(
    () => [
      {
        title: 'Appt ID',
        dataIndex: 'appointment_id',
        key: 'appointment_id',
        render: (id: number) => <Link to={`/appointments/${id}`}>{id}</Link>
      },
      {
        title: 'Creation date',
        dataIndex: 'created_at',
        key: 'created_at'
      },
      {
        title: 'Files',
        dataIndex: 'file',
        key: 'file',
        render: (value: string) => {
          return (
            <DownloadFile
              value={value}
              patientId={
                appointment?.patient?.id != null
                  ? String(appointment.patient.id)
                  : null
              }
            />
          );
        }
      },
      {
        title: '',
        dataIndex: 'fileSize',
        key: 'fileSize',
        align: 'center'
      },
      {
        title: '',
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        render: (text: React.ReactNode) => (
          <Button
            type="text"
            style={{ fontSize: 20, lineHeight: 1, padding: 0 }}
          >
            {text}
          </Button>
        )
      }
    ],
    [appointment?.patient?.id]
  );

  return (
    <>
      <Card
        bordered={false}
        bodyStyle={{
          padding: '24px 0 0 40px',
          marginBottom: 32,
          boxShadow: '0px 4px 20px #E7EAF6'
        }}
      >
        <Title level={5} style={{ marginBottom: 16 }}>
          {t('appointments_details.files_documents')}
        </Title>
        <List
          itemLayout="horizontal"
          dataSource={files}
          style={{
            maxHeight: 200,
            overflowY: 'auto'
          }}
          renderItem={item => (
            <List.Item key={item.id}>
              <List.Item.Meta
                title={
                  <DownloadFile
                    value={item.file}
                    patientId={
                      appointment?.patient?.id != null
                        ? String(appointment.patient.id)
                        : null
                    }
                  />
                }
              />
            </List.Item>
          )}
        />
      </Card>
      <Card
        bordered={false}
        bodyStyle={{
          padding: '24px 0 0 40px',
          marginBottom: 32,
          boxShadow: '0px 4px 20px #E7EAF6'
        }}
      >
        <Title level={5} style={{ marginBottom: 16 }}>
          {t('appointments_details.attached_links')}
        </Title>
        <List
          itemLayout="horizontal"
          dataSource={links}
          style={{
            maxHeight: 200,
            overflowY: 'auto'
          }}
          renderItem={(item: string, index: number) => (
            <List.Item key={`link-item-${index}`}>
              <List.Item.Meta
                title={
                  <a href={validateLink(item)} target="_blank" rel="noreferrer">
                    {item}
                  </a>
                }
              />
            </List.Item>
          )}
        />
      </Card>
      <Title level={5} style={{ paddingLeft: 8, marginBottom: 24 }}>
        {t('appointments_details.previous_appointments_files')}
      </Title>
      <Table dataSource={previousFiles} columns={columns} rowKey="id" />
    </>
  );
};

export default Files;
